import React from 'react';
import Modal from 'react-modal';

import CurrentUser from 'auth/CurrentUser';
import GET_COACHING_SESSIONS_QUERY from 'coaching/queries/GetCoachingSessions.gql';

import BoxBody from 'components/BoxBody';
import { CancellationReasons } from 'coaching/components/CancellationReasons';
import Checkbox from 'components/Checkbox';
import ConfirmDialog from 'components/ConfirmDialog';
import CoachingConstants from 'coaching/CoachingConstants';
import { coordinatorDeleteCoachingSession } from 'coaching/actions/CoachingActions';
import { deleteCoachingSession } from 'coaching/actions/CoachingActions';
import Divider from 'components/Divider';
import EventTracking from 'lib/EventTracking';
import Grid from 'components/Grid';
import Icon from 'components/Icon';
import Link from 'components/Link';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import Text from 'components/Text';
import {
  CoachingSessionGQL,
  CoachingInstanceGQL,
} from 'coaching/containers/GetCoachingSessionsAndRecordingsContainer';
import { CoachingUserType } from 'globalTypes';
import { formatDateForCordinator, formatTimeForCoordinator } from 'lib/dateTimeFormat';
import { wouldBeLateCancellation } from 'coaching/components/CoachingHomeView/CoachingSessionInstanceRowComponents';

export type Props = {
  session: CoachingSessionGQL;
  participantUuid: string;
  userName: string;
  userType: CoachingUserType;
  isPastEvent: boolean;
};

type State = {
  pageNumber: number;
  confirmDeleteDialog?: {
    ongoingSeries: boolean;
    coachingSessionInstanceUuid?: string;
    instanceStartDateTime?: string;
    instanceDateString?: string;
    cancellationReason?: string;
  };
  isActuallyLateCancellation: boolean | undefined;
};

export default class CoachingSessionRow extends React.Component<Props, State> {
  state: State = {
    pageNumber: 1,
    confirmDeleteDialog: undefined,
    isActuallyLateCancellation: undefined,
  };

  hasUpdatedCancellationReasons = () => {
    const currentUser = CurrentUser.getUser();
    return currentUser.hasFeature('updated_cancellation_reasons.coaching.june-21');
  };

  trackEvent(event: string) {
    EventTracking.trackKey(`coaching.coordinator_tool.participant_sidebar.${event}`);
  }

  formatDate = (dateString: string) => {
    return formatDateForCordinator(dateString, 'ddd, MMM D');
  };

  wrapWithDeleteAction = (
    element: React.ReactElement,
    hasMultipleInstances: boolean,
    isInPast: boolean,
    isRowHeader: boolean,
    instance?: CoachingInstanceGQL
  ) => {
    // We shouldn't show the session-level delete button if it's in the past and there are multiple instances
    const shouldHideDeleteButton = isInPast && hasMultipleInstances;

    const handleDeleteAction = () => {
      if (instance) {
        this.trackEvent('remove_session_instance.click');
      } else {
        this.trackEvent('remove_session.click');
      }

      this.setState(
        {
          confirmDeleteDialog: {
            ongoingSeries: hasMultipleInstances,
            instanceDateString: instance?.startDateTime
              ? this.formatDate(instance.startDateTime)
              : undefined,
            instanceStartDateTime: instance?.startDateTime,
            coachingSessionInstanceUuid: instance?.eventId,
          },
        },
        () => EventTracking.trackKey('coaching.coordinator_tool.remove_session_confirm_dialog.view')
      );
    };

    return shouldHideDeleteButton ? (
      <React.Fragment>{element}</React.Fragment>
    ) : (
      <Link
        data-test={`delete-action${isRowHeader ? '-rowHeader' : ''}`}
        color="chalkboard"
        noHover
        onClick={handleDeleteAction}>
        <MediaObject figure="right" center>
          {element}
          <Icon className="CoachingSessionRow-delete" name="x" size="small" />
        </MediaObject>
      </Link>
    );
  };

  renderConfirmDeleteDialog = () => {
    const { confirmDeleteDialog, isActuallyLateCancellation } = this.state;
    if (!confirmDeleteDialog) {
      return null;
    }

    const {
      cancellationReason,
      coachingSessionInstanceUuid,
      instanceDateString,
      instanceStartDateTime,
      ongoingSeries,
    } = confirmDeleteDialog;

    const { session } = this.props;

    const textConstants = ongoingSeries
      ? CoachingConstants.DELETE_COACHING_SESSION_MODAL_ONGOING
      : CoachingConstants.DELETE_COACHING_SESSION_MODAL_SINGLE;

    // rely on instanceStartDateTime if it's passed in, and the startDateTime of the next instance of the schedule if it's not
    const isLateCancellation = wouldBeLateCancellation(
      instanceStartDateTime ?? session.instances[0].startDateTime
    );
    const needsCancellationLatenessConfirmed =
      isLateCancellation && this.cancellationReasonNeedsLateConfirmation(cancellationReason);

    const hasUpdatedCancellationReasons = this.hasUpdatedCancellationReasons();
    const cancellationLatenessConfirmedIfNeeded = needsCancellationLatenessConfirmed
      ? isActuallyLateCancellation !== undefined
      : true;
    const canDeleteCoachingSession = hasUpdatedCancellationReasons
      ? cancellationReason && cancellationLatenessConfirmedIfNeeded
      : cancellationReason;

    return (
      <ConfirmDialog
        data-test="confirm-delete-dialog"
        actionButtonColor="apple"
        cancelButtonColor="apple"
        actionButtonText={textConstants.confirmText}
        confirmEventKey="coaching.coordinator_tool.remove_session_confirm_dialog.remove_session.click"
        confirmDisabled={!canDeleteCoachingSession}
        cancelButtonText={textConstants.cancelText}
        cancelEventKey="coaching.coordinator_tool.remove_session_confirm_dialog.keep_session.click"
        preConfirmPromise={() =>
          hasUpdatedCancellationReasons
            ? coordinatorDeleteCoachingSession(
                session.uuid,
                cancellationReason!,
                // this gets passed in as the value of waiveCustomerLateCharge.
                // isActuallyLateCancellation should be defined whenever isLateCancellation
                // is true, but guarding against the possibility it's not here
                isLateCancellation ? !(isActuallyLateCancellation ?? true) : false,
                coachingSessionInstanceUuid,
                [
                  {
                    query: GET_COACHING_SESSIONS_QUERY,
                    variables: {
                      userUuid: this.props.participantUuid,
                      userType: this.props.userType,
                    },
                  },
                ]
              )
            : deleteCoachingSession(
                this.props.session.uuid,
                cancellationReason,
                coachingSessionInstanceUuid,
                instanceStartDateTime,
                [
                  {
                    query: GET_COACHING_SESSIONS_QUERY,
                    variables: {
                      userUuid: this.props.participantUuid,
                      userType: this.props.userType,
                    },
                  },
                ]
              )
        }
        onRequestHide={() =>
          this.setState({ confirmDeleteDialog: undefined, isActuallyLateCancellation: undefined })
        }
        size="xsmall"
        title={<Text font="serif">{textConstants.headerText}</Text>}>
        <Text align="left">
          <Row size="xxsmall">
            <Text weight="semibold">{this.props.userName}</Text>
          </Row>
          {instanceDateString ?? null}
          {this.renderHeader()}
          {this.renderMetadata()}
          <Divider size="medium" />
          <CancellationReasons
            sessionType={ongoingSeries ? 'recurringSchedule' : 'instance'}
            changeState={(value: string) =>
              this.setState({
                confirmDeleteDialog: {
                  ...confirmDeleteDialog,
                  cancellationReason: value,
                },
              })
            }
          />
          {hasUpdatedCancellationReasons &&
            cancellationReason &&
            needsCancellationLatenessConfirmed &&
            this.renderConfirmLateCancellationPrompt(ongoingSeries)}
        </Text>
      </ConfirmDialog>
    );
  };

  cancellationReasonNeedsLateConfirmation = (cancellationReason?: string) => {
    // customer-side cancellation reasons we want coordinators to confirm lateness for
    const reasonsNeedingLateConfirmation = [
      'participant_requested_coach_left_platform',
      'participant_requested_reschedule',
      'help_form_requested',
    ];
    return cancellationReason && reasonsNeedingLateConfirmation.includes(cancellationReason);
  };

  renderHeader = () => {
    const { session } = this.props;
    const headerText =
      session.instances.length > 1 ? 'Ongoing' : this.formatDate(session.startDateTime);
    return (
      <Row size="xxsmall">
        <Text truncate size="small">
          <MediaObject center gutter="small">
            <Text>{headerText}</Text>
            <Text color="graphite">{session.coach.name}</Text>
          </MediaObject>
        </Text>
      </Row>
    );
  };

  renderMetadata = () => {
    const { session } = this.props;
    const startTime = formatTimeForCoordinator(session.startDateTime);
    return <Text size="small">{`${startTime} • ${session.duration} min `}</Text>;
  };

  renderConfirmLateCancellationPrompt = (ongoingSeries: boolean) => {
    const { isActuallyLateCancellation } = this.state;
    return (
      <BoxBody size="xsmall" dark rounded data-test="confirm-late-cancellation-prompt">
        <Row size="xsmall">
          <Text size="xsmall">
            {ongoingSeries ? 'There' : 'This'} is a session starting within 24 hours. Please confirm
            if this is a late cancellation.
          </Text>
        </Row>
        <Row size="xsmall">
          <Text size="xsmall" color="graphite">
            Select one (required)
          </Text>
        </Row>
        <Row size="small">
          <Grid columns={2}>
            <Checkbox
              checked={!!isActuallyLateCancellation}
              label="Yes"
              onChange={() => this.setState({ isActuallyLateCancellation: true })}
              data-test="yes-late-cancellation-checkbox"></Checkbox>
            <Checkbox
              // makes the initial state of both checkboxes unchecked instead of defaulting to one
              checked={
                isActuallyLateCancellation === undefined ? false : !!!isActuallyLateCancellation
              }
              label="No"
              onChange={() => this.setState({ isActuallyLateCancellation: false })}
              data-test="not-late-cancellation-checkbox"></Checkbox>
          </Grid>
        </Row>
      </BoxBody>
    );
  };

  renderInstances = () => {
    const {
      session: { instances },
    } = this.props;
    if (instances.length < 2) {
      return null;
    }

    const numberOfInstancesToShow =
      this.state.pageNumber * CoachingConstants.COACHING_SESSIONS_PAGE_LENGTH;
    const instancesToShow = instances.slice(0, numberOfInstancesToShow);
    return (
      <Row size="xsmall">
        <BoxBody size="xxsmall">
          {instancesToShow.map(instance => {
            const dateString = this.formatDate(instance.startDateTime);
            return (
              <div
                className="CoachingSessionRow-instance"
                key={instance.eventId}
                data-test="coaching-session-instance">
                {this.wrapWithDeleteAction(
                  <Text element="div">{dateString}</Text>,
                  false,
                  this.props.isPastEvent,
                  false,
                  instance
                )}
              </div>
            );
          })}
          {this.state.pageNumber === 1 &&
            instances.length > CoachingConstants.COACHING_SESSIONS_PAGE_LENGTH && (
              <Text className="CoachingSessionRow-showMore" element="div" weight="semibold">
                <Link
                  onClick={() => {
                    this.trackEvent('session_show_more_instances.click');
                    this.setState({ pageNumber: 2 });
                  }}>
                  Show more
                </Link>
              </Text>
            )}
        </BoxBody>
      </Row>
    );
  };

  sessionLevelDeletableElement = () => {
    // If the session is in the past and there's only one instance left, we want to delete that instance
    // rather than the whole series
    const instanceToDelete =
      this.props.isPastEvent && this.props.session.instances.length == 1
        ? this.props.session.instances[0]
        : undefined;
    return this.wrapWithDeleteAction(
      <div>
        {this.renderHeader()}
        {this.renderMetadata()}
      </div>,
      this.props.session.instances.length > 1,
      this.props.isPastEvent,
      true,
      instanceToDelete
    );
  };

  render() {
    return (
      <React.Fragment>
        <Modal data-test="confirm-delete-modal" isOpen={!!this.state.confirmDeleteDialog}>
          {this.renderConfirmDeleteDialog()}
        </Modal>
        <div className="CoachingSessionRow">
          <BoxBody size="xxsmall" className="CoachingSessionRow-header">
            {this.sessionLevelDeletableElement()}
          </BoxBody>
          {this.renderInstances()}
        </div>
      </React.Fragment>
    );
  }
}
